import { Set, Map, Record } from 'immutable';
import { createSelector } from 'reselect';
import { selectMap, selectTileJSON } from './base';

const SectionProperties = Record({ section: null });
const RowProperties = Record({ section: null, row: null });

export const Section = feature => SectionProperties(feature.properties || feature);
export const Row = feature => RowProperties(feature.properties || feature);

export const selectOriginalListings = createSelector(
  createSelector(selectMap, map => map.listings),
  listings => (listings || []).reduce(
    (map, listing) => map.mergeDeep(Map().set(Row(listing), Set([listing]))),
    Map(),
  ),
);

export const selectOriginalListingsBySection = createSelector(
  selectOriginalListings,
  listings => listings.reduce(
    (map, rowListings) => map.mergeDeep(Map().set(Section(rowListings.first()), rowListings)),
    Map(),
  ),
);

const selectRowCenters = createSelector(
  createSelector(selectTileJSON, json => json && json.rowCenters),
  centers => (centers || []).reduce(
    (map, [section, row, coordinates]) => map
      .set(Row({ section, row }), coordinates),
    Map(),
  ),
);

const listingTypes = [
  'bad-split',
  'bad',
  'good',
  'better',
  'best',
];

const betterListing = (left, right) => {
  if (left && !right) return left;
  if (right && !left) return right;
  if (!left && !right) return null;
  if (left.price !== right.price) return left.price < right.price ? left : right;
  return listingTypes.indexOf(left.quality) > listingTypes.indexOf(right.quality)
    ? left
    : right;
};

export const selectListings = createSelector(
  [selectOriginalListings, selectRowCenters],
  (ogListings, centers) => ogListings.flatMap((ogRowListings, key) => {
    const coordinates = centers.get(key);
    const lowestPriceListing = ogRowListings.reduce(betterListing);
    const listing = {
      coordinates,
      ...lowestPriceListing,
    };

    return ogRowListings.reduce(
      (map, { id }) => map.set(id, listing),
      Map().set(key, listing),
    );
  }),
);

export const selectListingsSet = createSelector(selectListings, listings => listings.toSet());

export const selectListingMarkersSet = createSelector(
  selectListingsSet,
  listings => listings
    .map((listing) => {
      const price = Math.floor(listing.price);
      return price && listing.quality && listing.coordinates
        ? { ...listing, price }
        : null;
    })
    .delete(null)
    .toArray(),
);

export const selectCategorizedListingMarkers = createSelector(
  selectListingsSet,
  listings => listings
    .filter(listing => listing.coordinates && listing.quality && listing.price)
    .reduce(
      (map, listing) => map.mergeDeep({ [listing.quality]: [listing] }),
      Map(),
    ).toJS(),
);

const getSectionFilter = (listings) => {
  const sections = Set(listings.map(({ section }) => section));
  if (!sections.size) return ['case', true, false, false];

  return ['all',
    ['==', ['geometry-type'], 'Polygon'],
    ['==', ['get', 'type'], 'section'],
    ['has', 'section'],
    ['match', ['get', 'section'], sections.toJS(), true, false],
  ];
};

const getRowFilter = (listings) => {
  const sectionRows = listings.reduce(
    (map, { section, row }) => map.mergeDeep(Map({ [section]: Set([row]) })),
    Map(),
  );

  if (!sectionRows.size) return ['case', true, false, false];

  return ['all',
    ['==', ['geometry-type'], 'Polygon'],
    ['==', ['get', 'type'], 'row'],
    ['has', 'section'],
    ['has', 'row'],
    ['any',
      ...sectionRows.entrySeq().map(([section, rows]) => ['all',
        ['==', ['get', 'section'], section],
        ['match', ['get', 'row'], rows.toJS(), true, false],
      ]).toJS(),
    ],
  ];
};

export const selectListingSectionsFilter = createSelector(
  createSelector(selectListingsSet, listings => listings.map(Section)),
  getSectionFilter,
);
export const selectListingRowsFilter = createSelector(
  createSelector(selectListingsSet, listings => listings.map(Row)),
  getRowFilter,
);

const selectFavorites = createSelector(
  createSelector(selectMap, map => map.favorites),
  selectListings,
  (favorites, listings) => Set(favorites || [])
    .map(id => listings.get(id) || null)
    .remove(null),
);

export const selectFavoriteRows = createSelector(
  selectFavorites,
  favorites => favorites.map(Row),
);

export const selectFavoriteSections = createSelector(
  selectFavorites,
  favorites => favorites.map(Section),
);

export const selectFavoriteListingSectionsFilter = createSelector(
  selectFavoriteSections,
  getSectionFilter,
);
export const selectFavoriteListingRowsFilter = createSelector(selectFavoriteRows, getRowFilter);

export const selectSelected = createSelector(
  createSelector(selectMap, map => map.selected),
  selectListings,
  (id, listings) => listings.get(id) || null,
);

export const selectSelectedRow = createSelector(
  selectSelected,
  selected => selected && Row(selected),
);

export const selectSelectedSection = createSelector(
  selectSelected,
  selected => selected && Section(selected),
);

const selectedSet = createSelector(
  selectSelected,
  selected => Set([selected]).remove(null).map(Row),
);

export const selectSelectedListingSectionFilter = createSelector(selectedSet, getSectionFilter);
export const selectSelectedListingRowFilter = createSelector(selectedSet, getRowFilter);



// WEBPACK FOOTER //
// ./src/modules/map/selectors/listings.js